import { type Vehicle, type UpdateVehicleRequest } from 'src/api/fm/vehicles/vehicles.model'
import { type RideHistoryItem } from 'src/api/fm/rentals/rentals.model'
import { type Task } from 'src/api/fm/tasks/tasks.model'
import {
    GET_VEHICLE,
    GET_VEHICLE_ERROR,
    RESET_VEHICLE_STATE,
    UPDATE_VEHICLE_INFO,
    SET_CURRENT_VEHICLE,
    SET_VEHICLE_RIDE_HISTORY,
    SET_VEHICLE_TASK_HISTORY,
    REMOVE_VEHICLE_FROM_REPAIR,
    BIKE_UNARM_LOCK,
    GET_VEHICLE_TASK_HISTORY,
    GET_VEHICLE_TASK_HISTORY_NEXT_PAGE,
    RESET_VEHICLE_TASK_HISTORY,
    GET_VEHICLE_RIDE_HISTORY,
    SET_VEHICLE_RIDE_HISTORY_DATE_FILTER,
    SET_VEHICLE_RIDE_HISTORY_PAGINATION,
    SET_CURRENT_VEHICLE_RIDER,
    RESET_SCOOTER_RIDE_HISTORY,
    GET_VEHICLE_LICENSE_PLATE_SUCCESS,
    DECOMMISSION_VEHICLE,
    SET_END_OF_TASK_HISTORY_PAGINATION,
    INCREMENT_TASK_HISTORY_PAGINATION,
    type VehicleCurrentRider,
    GET_VEHICLE_DECOMMISSIONED_INFO,
    SET_VEHICLE_DECOMMISSIONED_INFO,
    UNDO_DECOMMISSION_VEHICLE,
    SET_IS_FETCHING_VEHICLE_TASK_HISTORY,
    SOUND_ALARM,
    LOCK_VEHICLE,
    OPEN_HATCH,
    UNLOCK_VEHICLE,
    REBOOT_CCU,
    REBOOT_IOT,
    UPDATE_VERSION,
    TAKE_OWNERSHIP,
    SET_STATUS,
    GIVE_UP_OWNERSHIP,
    REPLACE_IOT,
} from 'src/redux/scooter/scooter.types'
import { type VehicleDecommissionedInfo, type DecommissionReason } from 'src/api/fm/decommission/decommission.model'

export type GetVehicleAction<T extends VehicleAction['type']> = Extract<VehicleAction, { type: T }>

export type VehicleAction =
    | ReturnType<typeof getVehicle>
    | ReturnType<typeof getVehicleError>
    | ReturnType<typeof setCurrentVehicle>
    | ReturnType<typeof setRideHistory>
    | ReturnType<typeof setVehicleTaskHistory>
    | ReturnType<typeof setIsFetchingVehicleTaskHistory>
    | ReturnType<typeof resetVehicleState>
    | ReturnType<typeof updateVehicleInfo>
    | ReturnType<typeof removeVehicleFromRepair>
    | ReturnType<typeof bikeUnarmLock>
    | ReturnType<typeof getVehicleTaskHistory>
    | ReturnType<typeof getVehicleTaskHistoryNextPage>
    | ReturnType<typeof resetVehicleTaskHistory>
    | ReturnType<typeof getVehicleRideHistory>
    | ReturnType<typeof setVehicleRideHistoryDateFilter>
    | ReturnType<typeof setVehicleRideHistoryPagination>
    | ReturnType<typeof setCurrentVehicleRider>
    | ReturnType<typeof resetScooterRideHistory>
    | ReturnType<typeof incrementTaskHistoryPagination>
    | ReturnType<typeof setEndOfTaskHistoryPagination>
    | ReturnType<typeof getVehicleLicensePlateSuccess>
    | ReturnType<typeof decommissionVehicle>
    | ReturnType<typeof undoDecommissionVehicle>
    | ReturnType<typeof getVehicleDecommissionedInfo>
    | ReturnType<typeof setVehicleDecommissionedInfo>
    | ReturnType<typeof soundAlarm>
    | ReturnType<typeof lockVehicle>
    | ReturnType<typeof openBatteryHatch>
    | ReturnType<typeof unlockVehicle>
    | ReturnType<typeof rebootCCU>
    | ReturnType<typeof rebootIOT>
    | ReturnType<typeof updateVersion>
    | ReturnType<typeof takeOwnership>
    | ReturnType<typeof setVehicleStatus>
    | ReturnType<typeof giveUpOwnership>
    | ReturnType<typeof replaceIot>

export const getVehicle = (id: string) => {
    return {
        type: GET_VEHICLE,
        payload: id,
    }
}

export const getVehicleError = (error: string) => {
    return {
        type: GET_VEHICLE_ERROR,
        payload: error,
    }
}

export const setCurrentVehicle = (vehicle: Vehicle) => {
    return {
        type: SET_CURRENT_VEHICLE,
        payload: vehicle,
    }
}

export const setRideHistory = (data: RideHistoryItem[]) => {
    return {
        type: SET_VEHICLE_RIDE_HISTORY,
        payload: data,
    }
}

export const setVehicleTaskHistory = (data: Task[]) => {
    return {
        type: SET_VEHICLE_TASK_HISTORY,
        payload: data,
    }
}

export const setIsFetchingVehicleTaskHistory = (isFetching: boolean) => {
    return {
        type: SET_IS_FETCHING_VEHICLE_TASK_HISTORY,
        payload: isFetching,
    }
}

export const resetVehicleState = () => {
    return {
        type: RESET_VEHICLE_STATE,
    }
}

export const updateVehicleInfo = (vehicleId: string, data: UpdateVehicleRequest) => {
    return {
        type: UPDATE_VEHICLE_INFO,
        payload: {
            vehicleId,
            data,
        },
    }
}

export const removeVehicleFromRepair = (scooter: { [key: string]: any }) => {
    return {
        type: REMOVE_VEHICLE_FROM_REPAIR,
        payload: { scooter },
    }
}

export const bikeUnarmLock = (bikeId: string) => {
    return {
        type: BIKE_UNARM_LOCK,
        payload: bikeId,
    }
}

export const getVehicleTaskHistory = () => ({
    type: GET_VEHICLE_TASK_HISTORY,
})

export const getVehicleTaskHistoryNextPage = () => ({
    type: GET_VEHICLE_TASK_HISTORY_NEXT_PAGE,
})

export const resetVehicleTaskHistory = () => ({
    type: RESET_VEHICLE_TASK_HISTORY,
})

export const getVehicleRideHistory = (cursor: string) => ({
    type: GET_VEHICLE_RIDE_HISTORY,
    payload: cursor,
})

export const setVehicleRideHistoryDateFilter = (date: string) => ({
    type: SET_VEHICLE_RIDE_HISTORY_DATE_FILTER,
    payload: date,
})

export const setVehicleRideHistoryPagination = (hasMore: boolean, nextCursor: string) => ({
    type: SET_VEHICLE_RIDE_HISTORY_PAGINATION,
    payload: { hasMore, nextCursor },
})

export const setCurrentVehicleRider = (payload: VehicleCurrentRider | null) => ({
    type: SET_CURRENT_VEHICLE_RIDER,
    payload,
})

export const resetScooterRideHistory = () => ({
    type: RESET_SCOOTER_RIDE_HISTORY,
})

export const incrementTaskHistoryPagination = () => ({
    type: INCREMENT_TASK_HISTORY_PAGINATION,
})

export const setEndOfTaskHistoryPagination = () => ({
    type: SET_END_OF_TASK_HISTORY_PAGINATION,
})

export const getVehicleLicensePlateSuccess = (licensePlate: string | null) => ({
    type: GET_VEHICLE_LICENSE_PLATE_SUCCESS,
    payload: { licensePlate },
})

export const decommissionVehicle = (vehicleId: string, reason: DecommissionReason) => ({
    type: DECOMMISSION_VEHICLE,
    payload: { vehicleId, reason },
})

export const undoDecommissionVehicle = (vehicleId: string) => ({
    type: UNDO_DECOMMISSION_VEHICLE,
    payload: { vehicleId },
})

export const getVehicleDecommissionedInfo = (vehicleId: string) => ({
    type: GET_VEHICLE_DECOMMISSIONED_INFO,
    payload: { vehicleId },
})

export const setVehicleDecommissionedInfo = (vehicleDecommissionedInfo: VehicleDecommissionedInfo) => ({
    type: SET_VEHICLE_DECOMMISSIONED_INFO,
    payload: vehicleDecommissionedInfo,
})

export const soundAlarm = (vehicleId: string) => ({
    type: SOUND_ALARM,
    payload: { vehicleId },
})

export const lockVehicle = (vehicleId: string) => ({
    type: LOCK_VEHICLE,
    payload: { vehicleId },
})

export const openBatteryHatch = (vehicleId: string) => ({
    type: OPEN_HATCH,
    payload: { vehicleId },
})

export const unlockVehicle = (vehicleId: string) => ({
    type: UNLOCK_VEHICLE,
    payload: { vehicleId },
})

export const rebootCCU = (vehicleId: string) => ({
    type: REBOOT_CCU,
    payload: { vehicleId },
})

export const rebootIOT = (vehicleId: string) => ({
    type: REBOOT_IOT,
    payload: { vehicleId },
})

export const updateVersion = (vehicleId: string) => ({
    type: UPDATE_VERSION,
    payload: { vehicleId },
})

export const takeOwnership = (vehicleId: string) => ({
    type: TAKE_OWNERSHIP,
    payload: { vehicleId },
})

export const setVehicleStatus = (vehicleId: string, status: string) => {
    return {
        type: SET_STATUS,
        payload: {
            vehicleId,
            status,
        },
    }
}

export const giveUpOwnership = (vehicleId: string) => ({
    type: GIVE_UP_OWNERSHIP,
    payload: { vehicleId },
})

export const replaceIot = (vehicleId: string, imei: string) => ({
    type: REPLACE_IOT,
    payload: { vehicleId, imei },
})
